/**
 * ORO Error Message Mapping
 * Maps backend error codes to user-friendly messages
 *
 * Tone: Calm, human, never alarmist, never blame the user
 *
 * Backend standardization (Issue #657):
 * - All errors use SCREAMING_SNAKE_CASE codes
 * - Domain-specific codes (e.g., CLUB_NOT_FOUND, EVENT_NOT_FOUND)
 * - Consistent error envelope: { error: { code, message, details? } }
 */

/**
 * Convert backend error code to user-friendly message
 *
 * @param code - Error code from backend (e.g., 'CLUB_NOT_FOUND', 'UNAUTHORIZED')
 * @param defaultMessage - Fallback message from backend or generic message
 * @returns User-friendly error message suitable for display
 *
 * @example
 * ```typescript
 * const message = getUserFacingError(apiError.code, apiError.message);
 * setError(message);
 * ```
 */
export function getUserFacingError(code: string, defaultMessage: string): string {
  const messages: Record<string, string> = {
    // ==========================================
    // Domain-Specific NOT_FOUND Codes
    // ==========================================
    'CLUB_NOT_FOUND': "We couldn't find that club",
    'EVENT_NOT_FOUND': "We couldn't find that event",
    'MEMBER_NOT_FOUND': "We couldn't find that member",
    'USER_NOT_FOUND': "We couldn't find that account",
    'MESSAGE_NOT_FOUND': "That message isn't available",
    'INVITE_NOT_FOUND': "That invite link isn't available",
    'TRAINING_GROUP_NOT_FOUND': "We couldn't find that training group",

    // ==========================================
    // Authentication & Authorization
    // ==========================================
    'UNAUTHORIZED': 'Please sign in to continue',
    'FORBIDDEN': "You don't have permission to do that",
    'SESSION_EXPIRED': 'Your session has expired. Please sign in again.',
    'INVALID_TOKEN': 'That sign-in link has expired. Request a new one to continue.',
    'INVITE_REQUIRED': 'This community requires an invite to join',

    // ==========================================
    // Validation Errors
    // ==========================================
    'VALIDATION_ERROR': defaultMessage, // Use backend's specific message
    'INVALID_EMAIL': 'Enter a valid email address',
    'INVALID_INPUT': 'Please review your input and try again',
    'MISSING_REQUIRED_FIELD': 'Required information is missing',
    'PROFANITY_DETECTED': 'Please keep language respectful',

    // ==========================================
    // Business Logic Errors
    // ==========================================
    'CONFLICT': 'This action conflicts with existing data',
    'DUPLICATE_ENTRY': 'This already exists',
    'ALREADY_MEMBER': "You're already a member of this club",
    'ALREADY_RSVP': "You've already responded to this event",
    'EVENT_FULL': 'This event has reached capacity',
    'PRIVATE_CLUB': 'This is a private club. An invite is required.',
    'GROUP_MEMBERSHIP_REQUIRED': 'Join this training group to continue',
    'GROUP_ALREADY_STARTED': "This training group has already started and can't be modified",
    'ALREADY_GROUP_MEMBER': "You're already part of this group",

    // ==========================================
    // Subscription Tier Errors (Issue #760)
    // ==========================================
    'SUBSCRIPTION_EXPIRED': "This feature isn't available on your current plan",
    'TIER_REQUIRED': 'This feature requires a higher plan',

    // ==========================================
    // Rate Limiting & Security
    // ==========================================
    'RATE_LIMITED': 'Too many requests. Please wait a moment and try again.',
    'BOT_CHECK_FAILED': 'Security check failed. Please try again.',
    'INVALID_ORIGIN': 'Request blocked for security reasons',

    // ==========================================
    // Server Errors
    // ==========================================
    'INTERNAL_ERROR': 'Something went wrong on our end',
    'SERVICE_UNAVAILABLE': 'Service is temporarily unavailable',
    'DATABASE_ERROR': 'A data error occurred. Please try again.',

    // ==========================================
    // Room & Chat Errors (Issue #727)
    // ==========================================
    'ROOM_NOT_FOUND': "That conversation isn't available",
    'ROOM_ACCESS_DENIED': "You don't have access to this conversation",
    'ROOM_FETCH_FAILED': "Conversations couldn't be loaded",

    // ==========================================
    // Network Errors
    // ==========================================
    'NETWORK_ERROR': 'Network connection issue detected',
    'TIMEOUT': 'The request took too long. Please try again.',
    'UNKNOWN_ERROR': 'An unexpected error occurred',
  };

  // Return mapped message or default
  return messages[code] || defaultMessage || 'Something went wrong. Please try again.';
}

/**
 * Check if an error code indicates a "not found" error
 * Useful for conditional rendering (show 404 page vs inline error)
 */
export function isNotFoundError(code: string): boolean {
  return code.endsWith('_NOT_FOUND') || code === 'NOT_FOUND';
}

/**
 * Check if an error code indicates an authentication issue
 * Useful for triggering login flows
 */
export function isAuthError(code: string): boolean {
  return ['UNAUTHORIZED', 'SESSION_EXPIRED', 'INVALID_TOKEN', 'INVITE_REQUIRED'].includes(code);
}

/**
 * Check if an error code indicates a validation issue
 * Useful for showing form field errors vs global errors
 */
export function isValidationError(code: string): boolean {
  return code === 'VALIDATION_ERROR' || code.startsWith('INVALID_');
}
